import { useFormContext } from 'react-hook-form';

import type { FormFields, FormInput } from 'models/sitecore';
import { filterObject } from 'utils/collection';
import { assertUnreachable } from 'utils/helpers';

export const getValuesForSubmit = (
	formInputs: FormInput[],
	formValues: Record<string, unknown>,
): Record<string, unknown> =>
	filterObject({ ...formValues }, (_val, key, obj) => {
		const field = formInputs.find(({ name }) => name === key);
		return field?.requires
			? Boolean(field.useData) && Boolean(obj[field.requires])
			: Boolean(field?.useData);
	});

// If a field is dependent on another field to have a value to render it has 'requires' set
// this hook checks if the dependent field has a value
export function useFieldDependencyStatus(
	dependencyFieldName: string | undefined,
) {
	const { watch } = useFormContext();
	return {
		dependencyFieldHasValue: dependencyFieldName
			? Boolean(watch(dependencyFieldName))
			: true,
	};
}

export function getDefaultValues(fields: FormFields | undefined) {
	const defaultValues = {};
	if (!fields) {
		return defaultValues;
	}
	fields.forEach((field) => {
		switch (field.type) {
			case 'TextField':
				defaultValues[field.name] = '';
				break;
			case 'SelectField':
				defaultValues[field.name] = field.options[0]?.value;
				break;
			case 'ConfirmField':
			case 'RadioField':
			case 'FileField':
			case 'SubmitButton':
			case 'InfoField':
			case 'HighlightField':
			case 'MetaField':
			case 'AuthField':
				break;
			default:
				assertUnreachable(field, 'field');
		}
	});

	return defaultValues;
}

export type StorageKey = 'ContactForm';

export function getSavedStateFor(key: StorageKey | undefined) {
	return key
		? (globalThis[key] as Record<string, unknown> | undefined)
		: undefined;
}
